import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ArrowIcon,
  Box,
  Button,
  CardTabs,
  Flex,
  Heading,
  ResponsiveTable,
  Tab,
  TabList,
  TabPanel,
  TabPanelList,
  Text,
  TextButton,
  Tooltip,
} from '@modusign/design-system';
import { getBatchJobList, getBatchJobTaskList } from '@modusign/openapi-client-external-document';
import moment from 'moment';
import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useInfiniteQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import HelpButton from '@app/components/HelpButton';
import ModusignLogo from '@app/components/ModusignLogo';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import workspaceService from '@app/libs/workspaceService';
import { Job } from '@app/store/transport-layer/external-document/external-documents/batches';

import translateDocumetnUpdateTaskFailedReason from '../../utils/translateDocumetnUpdateTaskFailedReason';

export default function BatchUpdateHistoryPage() {
  const history = useHistory();
  const { isPermitted: hasReadAllPermission } = useMemberPermissions('READ_ALL_EXTERNAL_DOCUMENT');

  return (
    <Flex flexDirection="column" minHeight="100vh">
      <Flex
        alignItems="center"
        as="header"
        bg="darkGray700"
        height="65px"
        justifyContent="space-between"
      >
        <Box as="button" flex="1 1 0" onClick={history.goBack}>
          <Flex alignItems="center" color="textNeutralLightest">
            <ArrowIcon direction="left" height="18px" mx={4} width="9px" />
            <ModusignLogo color="white" height={17} width={123} />
            <Text fontWeight="medium" ml={3}>
              외부문서
            </Text>
          </Flex>
        </Box>
        <Heading color="textNeutralLightest" flex="0 0 1" fontSize="sm" fontWeight="medium">
          계약 업데이트 내역
        </Heading>
        <Flex alignItems="center" flex="1 1 0" justifyContent="flex-end" mr={4}>
          <HelpButton variant="darkIcon" />
        </Flex>
      </Flex>
      <Box as="main" flex="auto" mt={10} mx="auto" width="min(768px, 100vw)">
        {hasReadAllPermission ? (
          <CardTabs>
            <TabList>
              <Tab>내 진행 내역</Tab>
              <Tab>전체 진행 내역</Tab>
            </TabList>
            <TabPanelList>
              <TabPanel>
                <UpdateHistoryList assignedFilter />
              </TabPanel>
              <TabPanel>
                <UpdateHistoryList assignedFilter={false} />
              </TabPanel>
            </TabPanelList>
          </CardTabs>
        ) : (
          <UpdateHistoryList assignedFilter />
        )}
      </Box>
    </Flex>
  );
}

function UpdateHistoryList({ assignedFilter }: { assignedFilter: boolean }) {
  const { createErrorHandler } = useOpenErrorModal();
  const workspaceId = useWorkspaceId();

  const [openedJobId, setOpenedJobId] = useState<string | null>(null);

  const {
    data,
    isLoading: isJobListLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['externalDocumentBatchUpdateJobs', { assignedFilter }],
    ({ pageParam }) =>
      getBatchJobList(workspaceId, {
        limit: 20,
        offset: pageParam,
        filter: {
          assigned: assignedFilter ? true : undefined,
        },
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        const fetchedCount = pages.reduce((acc, page) => acc + page.jobs.length, 0);

        return fetchedCount < lastPage.count ? fetchedCount : undefined;
      },
      refetchInterval: 3 * 1000,
      onError: createErrorHandler({ title: '업데이트 내역 불러오기 실패' }),
    }
  );

  const currentPage = data?.pages.length;
  const totalPages = Math.ceil((data?.pages[0].count || 0) / 20);
  const jobList = data?.pages.flatMap((j) => j.jobs);

  return (
    <Flex alignItems="stretch" flexDirection="column" mt={7}>
      <Flex
        borderBottom="2px solid"
        borderBottomColor="tableBorderNeutralBase"
        gap={6}
        px={6}
        py={4}
      >
        <Text color="textNeutralVeryLight" fontSize="sm" sx={{ flex: 'auto' }}>
          일시
        </Text>
        <Text
          color="textNeutralVeryLight"
          display={assignedFilter ? 'none' : 'block'}
          fontSize="sm"
          sx={{ width: '180px' }}
        >
          멤버
        </Text>
        <Text color="textNeutralVeryLight" fontSize="sm" sx={{ width: '120px' }}>
          상태
        </Text>
        <Text color="textNeutralVeryLight" fontSize="sm" sx={{ width: '136px' }}>
          결과 (성공 / 실패)
        </Text>
        <Text sx={{ width: '56px' }} />
      </Flex>
      {isJobListLoading && (
        <Box height="200px" position="relative" width="100%">
          <CenterLoadingSpinner size={40} />
        </Box>
      )}
      <ul>
        {jobList &&
          (jobList.length > 0 ? (
            jobList?.map((job) => (
              <JobAccordion
                displayMemberInfo={!assignedFilter}
                isOpen={job.id === openedJobId}
                job={job}
                key={job.id}
                onToggle={() => setOpenedJobId((id) => (id === job.id ? null : job.id))}
              />
            ))
          ) : (
            <Text
              display="flex"
              fontWeight="medium"
              sx={{ alignItems: 'center', justifyContent: 'center', height: '141px' }}
            >
              계약을 일괄 업데이트한 내역이 없습니다.
            </Text>
          ))}
      </ul>
      {hasNextPage && (
        <Button
          isLoading={isFetchingNextPage}
          mt={3}
          variant="subtle"
          width="100%"
          onClick={() => fetchNextPage()}
        >
          더 보기 ({currentPage}/{totalPages})
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '11px', marginLeft: '3px' }} />
        </Button>
      )}
    </Flex>
  );
}

interface JobAccordionProps {
  job: Job;
  isOpen: boolean;
  displayMemberInfo: boolean;
  onToggle: () => void;
}

function JobAccordion({ job, isOpen, displayMemberInfo, onToggle }: JobAccordionProps) {
  const {
    data: tasks,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery(
    ['externalDocumentBatchUpdateJobTasks', job.id],
    ({ pageParam }) =>
      getBatchJobTaskList(workspaceService.getWorkspaceId(), job.id, {
        limit: 10,
        offset: pageParam,
        filter: {
          status: 'FAILED',
        },
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        const fetchedCount = pages.reduce((acc, page) => acc + page.tasks.length, 0);

        return fetchedCount < lastPage.count ? fetchedCount : undefined;
      },
      enabled: isOpen,
    }
  );

  return (
    <Flex as="li" flexDirection="column" width="100%">
      <Flex
        borderBottom="1px solid"
        borderBottomColor="tableBorderNeutralLight"
        gap={6}
        px={6}
        py={5}
      >
        <Text sx={{ flex: 'auto' }}>{moment(job.createdAt).format('LLL')}</Text>
        <Box display={displayMemberInfo ? 'block' : 'none'} sx={{ width: '180px' }}>
          <Text as="div" ellipsis>
            {job.member?.name || '정보 없음'}
          </Text>
          <Text color="textNeutralVeryLight" fontSize="xs">
            {job.member?.email || '멤버를 찾을 수 없습니다.'}
          </Text>
        </Box>
        <Text sx={{ width: '120px' }}>{job.count.pending ? '진행 중' : '완료'}</Text>
        <Text sx={{ width: '136px' }}>
          {job.count.success.toLocaleString()} / {job.count.failure.toLocaleString()}
        </Text>
        <Tooltip
          content="확인할 실패 내역이 없습니다."
          disabled={job.count.failure !== 0}
          placement="top"
        >
          <TextButton disabled={job.count.failure === 0} sx={{ width: '56px' }} onClick={onToggle}>
            자세히
          </TextButton>
        </Tooltip>
      </Flex>
      {isOpen && (
        <Box
          bg="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          maxHeight="360px"
          overflowY="auto"
          px={6}
          py={4}
        >
          <InfiniteScroll
            element="tbody"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={() => fetchNextPage()}
            loader={
              <Box as="tr" height="50px" my="4" position="relative">
                <td colSpan={3}>
                  <CenterLoadingSpinner size={30} />
                </td>
              </Box>
            }
            style={{ height: '100%' }}
            threshold={100}
            useWindow={false}
          >
            <ResponsiveTable.Table sx={{ tableLayout: 'fixed' }}>
              <ResponsiveTable.THead>
                <ResponsiveTable.THeadRow>
                  <ResponsiveTable.TH sx={{ px: 0 }}>업데이트 실패한 계약</ResponsiveTable.TH>
                  <ResponsiveTable.TH color="textNegativeBase" sx={{ width: '208px', px: 0 }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '4px' }} />
                    실패 사유
                  </ResponsiveTable.TH>
                </ResponsiveTable.THeadRow>
              </ResponsiveTable.THead>
              <tbody>
                {isLoading && (
                  <tr>
                    <Box as="td" colSpan={3} height="80px" position="relative" width="100%">
                      <CenterLoadingSpinner size={20} />
                    </Box>
                  </tr>
                )}
                {tasks?.pages
                  .flatMap((t) => t.tasks)
                  .map((task) => {
                    if (task.failedReason) {
                      return (
                        <Box
                          as="tr"
                          borderBottom="1px solid"
                          borderBottomColor="#e4e4e4"
                          key={task.id}
                        >
                          <Box
                            as="td"
                            fontWeight="medium"
                            pr={6}
                            py={4}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {task.document.title}
                          </Box>
                          <Box
                            as="td"
                            color="textNeutralLight"
                            fontSize="sm"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {translateDocumetnUpdateTaskFailedReason(task.failedReason.reason)}
                          </Box>
                        </Box>
                      );
                    }

                    return null;
                  })}
              </tbody>
            </ResponsiveTable.Table>
          </InfiniteScroll>
        </Box>
      )}
    </Flex>
  );
}
